import {
  JARVIS_CHROME_CAPABILITIES,
  jarvisCommandArgsSchema,
  type JarvisCommandToolArgs,
  type JarvisCommandToolMeta,
} from './jarvis-tools-v1.js';

/**
 * The COMMAND CENTER's Jarvis capability catalog (COMMAND-2 / ADR 0096) — the fleet-scope
 * console's parallel to the Idea Engine's `JARVIS_CAPABILITIES`. Same DSL, same drift-proof
 * shape, same security model (the CLIENT registry gates on effect; nothing here bypasses
 * the confirm gate). The `jarvis.*` chrome rows — including the self-approval denylist — are
 * the SHARED {@link JARVIS_CHROME_CAPABILITIES}, spread in; only the two console actions are
 * bespoke. Kept small on purpose: the bridge's operable surface is warp + view switching
 * (cross-service ACTIONS ride the ADR 0067 durable event hub once wired — ADR 0096 §3 / backlog CC-13).
 */

/** Grouping for the compact system-prompt index + discovery. */
export type CommandCenterCapabilityCategory = 'fleet' | 'view' | 'jarvis';

/** One CC capability = one command's agent metadata (mirrors idea-engine's `JarvisCapability`). */
export interface CommandCenterCapability extends JarvisCommandToolMeta {
  readonly category: CommandCenterCapabilityCategory;
  readonly agentOperable: boolean;
  /** Required when `agentOperable` is false — WHY the agent may not operate this command. */
  readonly reason?: string;
}

/**
 * THE CC CATALOG. `as const satisfies` keeps every literal (so the CC service can derive
 * per-command arg types + lock them against its CommandMap) while `satisfies` type-checks
 * every row against {@link CommandCenterCapability}.
 */
export const COMMAND_CENTER_CAPABILITIES = [
  // ── fleet navigation (semantic; act — a transient, operator-undoable navigation) ──
  {
    command: 'cc.warp',
    category: 'fleet',
    agentOperable: true,
    effect: 'act',
    title: 'Warp to a service',
    description:
      "Navigate the operator to one fleet service by its id (from ui.read.fleet — e.g. 'idea-engine', 'tracker'). Leaves the bridge for that service's console.",
    fields: [{ kind: 'string', name: 'service', description: 'The service id to warp to (from ui.read.fleet).', minLength: 1, maxLength: 60 }],
  },
  // ── view switching (act — changes only what the bridge shows) ──
  {
    command: 'cc.view',
    category: 'view',
    agentOperable: true,
    effect: 'act',
    title: 'Switch the bridge view',
    description: 'Switch the Command Center between its three views: the Bridge (fleet map), the Service Catalogue (list), or Artifacts.',
    fields: [{ kind: 'enum', name: 'view', description: 'Which view to show.', values: ['bridge', 'catalogue', 'artifacts'] }],
  },

  // ══ NON-OPERABLE — the shared jarvis.* chrome + self-approval denylist (ADR 0096) ══
  ...JARVIS_CHROME_CAPABILITIES,
] as const satisfies readonly CommandCenterCapability[];

/** Union of every catalogued CC command name (the CC service locks this against `keyof CommandMap`). */
export type CommandCenterCapabilityCommand = (typeof COMMAND_CENTER_CAPABILITIES)[number]['command'];

/** The agent-OPERABLE subset — the only commands `ui.command.invoke` will run on the bridge. */
export const COMMAND_CENTER_OPERABLE_CAPABILITIES = COMMAND_CENTER_CAPABILITIES.filter(
  (c) => c.agentOperable,
) as readonly CommandCenterCapability[];

const CC_CAPABILITY_BY_COMMAND: ReadonlyMap<string, CommandCenterCapability> = new Map(
  COMMAND_CENTER_CAPABILITIES.map((c) => [c.command, c]),
);

/** Look up one CC capability by command name (undefined ⇒ not a catalogued command). */
export const commandCenterCapability = (command: string): CommandCenterCapability | undefined =>
  CC_CAPABILITY_BY_COMMAND.get(command);

/** Derived arg types for the two operable CC commands (the service's payloads are assignable to these). */
export type CcWarpArgs = JarvisCommandToolArgs<Extract<(typeof COMMAND_CENTER_CAPABILITIES)[number], { command: 'cc.warp' }>>;
export type CcViewArgs = JarvisCommandToolArgs<Extract<(typeof COMMAND_CENTER_CAPABILITIES)[number], { command: 'cc.view' }>>;

/** The `view` enum values `cc.view` accepts (re-exported for the CC command handler's mapping). */
export const CC_VIEW_VALUES = ['bridge', 'catalogue', 'artifacts'] as const;
export type CcViewValue = (typeof CC_VIEW_VALUES)[number];

/**
 * Build the boundary Zod parser for one catalogued CC command (parse, don't validate — ADR
 * 0005). Undefined ⇒ the command is not in the catalog (caller refuses it as not_found). The
 * CC client registry uses this; tests use it to pin each command's arg contract.
 */
export function commandCenterArgsSchemaFor(command: string) {
  const cap = CC_CAPABILITY_BY_COMMAND.get(command);
  return cap ? jarvisCommandArgsSchema(cap) : undefined;
}
